// RichTextEditor.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import JoditEditor from 'jodit-react';

const MAX_WORDS = 10000;

// strip HTML and count words
function countWords(html) {
  const text = html.replace(/<[^>]+>/g, ' ').trim();
  return text ? text.split(/\s+/).length : 0;
}

// grab first `max` words from an HTML string (stripping tags)
function trimToMaxWords(html, max) {
  const text = html.replace(/<[^>]+>/g, ' ').trim();
  const words = text.split(/\s+/).slice(0, max);
  return words.join(' ');
}

export default function RichTextEditor({
  fieldName,
  fieldValue,
  setFieldValue,
}) {
  const editorRef = useRef(null);
  const [content, setContent] = useState(fieldValue || '');
  const [hasAlerted, setHasAlerted] = useState(false);

  // If parent ever resets fieldValue, reset our content & alert‐flag
  useEffect(() => {
    setContent(fieldValue || '');
    setHasAlerted(false);
  }, [fieldValue]);

  // onChange handler for Jodit: typing, images, paste, etc.
  const handleChange = useCallback(
    newContent => {
      // 1) Always keep the editor’s internal state in `content`
      setContent(newContent);

      // 2) Count words on the full HTML:
      const words = countWords(newContent);

      // 3) If over limit, show the alert once and send only trimmed text to Formik
      if (words > MAX_WORDS) {
        if (!hasAlerted) {
          alert(
            `لقد تجاوزت الحد الأقصى المسموح به (${MAX_WORDS} كلمة).\n` +
              `سيتم إرسال أوّل ${MAX_WORDS} كلمة فقط إلى الحفظ.`
          );
          setHasAlerted(true);
        }
        // Send only the first 10000 words to Formik
        const trimmed = trimToMaxWords(newContent, MAX_WORDS);
        setFieldValue(fieldName, trimmed);
      } else {
        // Under or exactly at limit → send full content
        setFieldValue(fieldName, newContent);
      }
    },
    [fieldName, setFieldValue, hasAlerted]
  );

  // We do NOT trim or block in beforePaste—let handleChange decide
  const config = useMemo(
    () => ({
      readonly: false,
      // no beforePaste trimming here
    }),
    []
  );

  return (
    <div>
      <JoditEditor
        ref={editorRef}
        value={content}
        config={config}
        tabIndex={1}
        onChange={handleChange}
      />
    </div>
  );
}

export { countWords, MAX_WORDS, trimToMaxWords };
